/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useRef, useState } from 'react'
import styled from 'styled-components'
import {
  QuestionMarkOutlined as QuestionMarkOutlinedIcon,
  CaretDown as CaretDownIcon,
  AppSwitcher as AppSwitcherIcon,
  Menu,
  Button,
  IconButton
  // @ts-ignore
} from '@netbasequid/core'
import { useSysTheme, useDisplayTheme } from 'hooks/useUiState'
import { SystemTheme } from 'hooks/useUiState/types'
import { sendMessage } from 'utils/iframeUtil'
import { useTranslation } from 'react-i18next'
import { useNavBarConfig, NavBarConfigType } from './hooks'

const NavBarContainer = styled.div<{ $theme: SystemTheme }>`
  height: 48px;
  display: flex;
  align-items: center;
  background-color: ${({ $theme }) => $theme.elevation[100].background};
  box-sizing: border-box;
  border-bottom: 1px solid ${({ $theme }) => $theme.common.divider[100]};
`
const Switcher = styled.div`
  width: 48px;
  height: 100%;

  display: flex;
  justify-content: center;
  align-items: center;
`

const TabContainer = styled.ul<{ $theme: SystemTheme }>`
  flex: 1;
  display: flex;
  list-style-type: none;
  gap: 8px;
  padding: 0;
  margin: 0;
  margin-left: 52px;
  height: 100%;
`
const Tab = styled.li<{ $theme: SystemTheme; $selected?: boolean }>`
  display: flex;
  align-items: center;
  padding: 0 8px;
  border-bottom: ${({ $theme, $selected }) =>
    `3px solid ${$selected ? $theme.text.primary : 'transparent'}`};
  color: ${({ $theme }) => $theme.text.primary};
  line-height: ${({ $theme }) => $theme.typescale[14].lineHeight};
  font-size: ${({ $theme }) => $theme.typescale[14].size};
  font-weight: ${({ $theme }) => $theme.fontWeight.semibold};
  font-family: var(--large-font-family);
  cursor: pointer;

  &:hover {
    background-color: ${({ $theme }) => $theme.interaction.onElevation.default};
  }
`

const HelpContainer = styled.div<{ $theme: SystemTheme }>`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;

  padding: 0 12px;
  height: 100%;
  cursor: pointer;

  &:hover {
    background-color: ${({ $theme }) => $theme.interaction.onElevation.default};
  }
`
const UserContainer = styled.div<{ $theme: SystemTheme }>`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;

  padding: 0px 14px 0 12px;
  height: 100%;
  cursor: pointer;

  &:hover {
    background-color: ${({ $theme }) => $theme.interaction.onElevation.default};
  }
`
const Info = styled.span<{ $theme: SystemTheme }>`
  line-height: ${({ $theme }) => $theme.typescale[14].lineHeight};
  font-size: ${({ $theme }) => $theme.typescale[14].size};
  font-weight: ${({ $theme }) => $theme.fontWeight.semibold};
  font-family: var(--large-font-family);
  color: ${({ $theme }) => $theme.text.primary};
  cursor: pointer;
`

const Divider = styled.div<{ $theme: SystemTheme }>`
  height: 1px;
  background-color: ${({ $theme }) => $theme.common.divider[100]};
`

const MenuInnerConatiner = styled.div`
  padding: 28px 0;
`
const QuidLogo = styled.img`
  padding: 0 28px;
  height: 22px;
`
const ProductItem = styled(Menu.MenuItem)`
  padding: 6px 28px;
`

const NAV_BAR_ACTION_KEY = {
  discover: 'discover',
  monitor: 'monitor',
  predict: 'predict',
  compete: 'compete',
  connect: 'connect',
  home: 'homeTab',
  dashboards: 'dashboardTab',
  topics: 'topicTab',
  analyze: 'analysisTab',
  edit: 'annotationTab',
  aiStudio: 'aiLabTab',
  quid: 'quid',
  pro: 'proTab',
  admin: 'adminTab',
  settings: 'settingBarSettings',
  logout: 'logout',
  needHelp: 'needHelp',
  socialAuthorization: 'socialAuthorization'
}

const ProductSwitcher = ({ onProductClick }: { onProductClick: (actionKey: string) => void }) => {
  const theme = useSysTheme()
  const mode = useDisplayTheme()
  const switcherRef = useRef(null)
  const [open, setOpen] = useState(false)
  const QUID_PRODUCTS = {
    discover: NAV_BAR_ACTION_KEY.discover,
    monitor: NAV_BAR_ACTION_KEY.monitor,
    predict: NAV_BAR_ACTION_KEY.predict,
    compete: NAV_BAR_ACTION_KEY.compete,
    connect: NAV_BAR_ACTION_KEY.connect
  }
  return (
    <>
      <Switcher ref={switcherRef} onClick={() => setOpen(true)}>
        <IconButton mode={mode} color="regular" variant="clickable">
          <AppSwitcherIcon color={theme.text.primary} />
        </IconButton>
      </Switcher>
      <Menu
        mode={mode}
        open={open}
        anchorEl={switcherRef.current}
        disablePortal
        paperMargin={{ top: '-4px', left: '6px' }}
        onClose={() => {
          setOpen(false)
        }}>
        <MenuInnerConatiner>
          <QuidLogo src={`${process.env.PUBLIC_URL}/assets/quid-logo-${mode}.svg`} alt="quid" />
          <Divider $theme={theme} style={{ margin: '18px 28px 12px 28px' }} />
          {Object.keys(QUID_PRODUCTS).map((product) => (
            <ProductItem key={product} onClick={() => onProductClick(product)}>
              <img
                src={`${process.env.PUBLIC_URL}/assets/switcher/quid-${product}-switcher-${mode}.svg`}
                alt={`quid ${product}`}
              />
            </ProductItem>
          ))}
        </MenuInnerConatiner>
      </Menu>
    </>
  )
}

const NavBar = () => {
  const theme = useSysTheme()
  const mode = useDisplayTheme()
  const [open, setOpen] = useState(false)
  const userRef = useRef(null)
  const [selectedTab, setSelectedTab] = useState('dashboardTab')
  const navBarConfig = useNavBarConfig()
  const { t } = useTranslation()
  const TABS = {
    home: {
      id: NAV_BAR_ACTION_KEY.home,
      displayKey: 'home'
    },
    dashboards: {
      id: NAV_BAR_ACTION_KEY.dashboards,
      displayKey: 'dashboard_other'
    },
    topics: {
      id: NAV_BAR_ACTION_KEY.topics,
      displayKey: 'topic_other'
    },
    analyze: {
      id: NAV_BAR_ACTION_KEY.analyze,
      displayKey: 'analyze'
    },
    edit: {
      id: NAV_BAR_ACTION_KEY.edit,
      displayKey: 'edit'
    },
    aiStudio: {
      id: NAV_BAR_ACTION_KEY.aiStudio,
      displayKey: 'AI Studio'
    },
    quid: {
      id: NAV_BAR_ACTION_KEY.quid,
      displayKey: 'Quid Discover'
    },
    pro: {
      id: NAV_BAR_ACTION_KEY.pro,
      displayKey: 'pro'
    }
  }
  const USER_ACTIONS = {
    admin: {
      id: NAV_BAR_ACTION_KEY.admin,
      displayKey: 'admin'
    },
    settings: {
      id: NAV_BAR_ACTION_KEY.settings,
      displayKey: 'settings'
    },
    logout: {
      id: NAV_BAR_ACTION_KEY.logout,
      displayKey: 'logOut'
    }
  }

  const handleNavItemClick = (actionKey: string) => {
    if (actionKey !== selectedTab) {
      sendMessage({ type: 'v4NavItemClick', data: actionKey })
    }
  }

  return (
    <NavBarContainer $theme={theme}>
      <ProductSwitcher onProductClick={handleNavItemClick} />
      <img src={`${process.env.PUBLIC_URL}/assets/quid-monitor-${mode}.svg`} alt="quid monitor" />
      <TabContainer $theme={theme}>
        {Object.values(TABS)
          .filter((tab) => navBarConfig.hasTabs[tab.id as keyof NavBarConfigType['hasTabs']])
          .map((tab) => (
            <Tab
              key={tab.id}
              $theme={theme}
              $selected={selectedTab === tab.id}
              onClick={() => {
                setSelectedTab(tab.id)
                handleNavItemClick(tab.id)
              }}>
              <a id={tab.id}>{t(tab.displayKey)}</a>
            </Tab>
          ))}
      </TabContainer>
      <HelpContainer $theme={theme} onClick={() => handleNavItemClick(NAV_BAR_ACTION_KEY.needHelp)}>
        <QuestionMarkOutlinedIcon color={theme.text.primary} />
        <Info $theme={theme}> {t('needHelp')}</Info>
      </HelpContainer>
      <UserContainer $theme={theme} ref={userRef} onClick={() => setOpen(true)}>
        <Info $theme={theme}>{navBarConfig.userName}</Info>
        <CaretDownIcon color={theme.text.primary} />
      </UserContainer>
      <Menu
        mode={mode}
        open={open}
        anchorEl={userRef.current}
        disablePortal
        paperMargin={{ top: '-4px', right: '6px' }}
        onClose={() => {
          setOpen(false)
        }}>
        {Object.values(USER_ACTIONS)
          .filter((ua) => (ua.id === 'adminTab' ? navBarConfig.hasTabs.adminTab : true))
          .map((ua) => (
            <Menu.MenuItem key={ua.id} onClick={() => handleNavItemClick(ua.id)}>
              {t(ua.displayKey)}
            </Menu.MenuItem>
          ))}
        <Divider $theme={theme} />
        <div style={{ padding: '10px' }}>
          <Button
            mode={mode}
            color="secondary"
            onClick={() => handleNavItemClick(NAV_BAR_ACTION_KEY.socialAuthorization)}>
            {t('socialAuthorization')}
          </Button>
        </div>
      </Menu>
    </NavBarContainer>
  )
}

export default NavBar
